import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationMenu } from './menu/NotificationMenu';
import { NotificationContent } from './NotificationContent';
import { doctrineAnalytics } from '../../../utils/analyticsUtils';
import { NavItemButton } from '../../components/item/NavItem';
import { useUser } from '../../../user/useUser';
import { StateType } from '../../../state/reducers';
import { notificationsDisplayed, fetchNotifications } from '../state/actions';

type Props = {
  cancelHover: Function;
};

const reloadNotificationsDelayMs = 300000;

export const Notification: FC<Props> = ({ cancelHover }) => {
  const [showNav, setShowNav] = useState(false);
  const currentNotificationsLoadIntervalId = useRef<number | null>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const loading = useRef(false);
  const { loggedIn } = useUser();

  const { notifList } = useSelector((state: StateType) => ({
    notifList: state.notifications.notifList,
  }));
  const dispatch = useDispatch();
  const onDisplayed = async (lastNotifShownId: number) => {
    await dispatch(notificationsDisplayed(lastNotifShownId));
  };
  const onLoad = async () => {
    await dispatch(fetchNotifications());
  };

  const showNotificationCallback = useCallback(
    (event: any) => {
      if (
        notificationRef &&
        notificationRef.current &&
        !notificationRef.current.contains(event.target) &&
        showNav
      ) {
        setShowNav(false);
        cancelHover(false, 'notification');
      }
    },
    [showNav],
  );

  const openNotifications = async () => {
    if (showNav) {
      setShowNav(false);
      cancelHover(false, 'notification');
    } else {
      doctrineAnalytics('Notification ButtonClicked');
      setShowNav(true);
      cancelHover(true, 'notification');

      let lastNotifShown = 0;
      notifList.forEach((notif) => {
        const id: number = +notif.id;
        if (!Number.isNaN(id) && id > lastNotifShown) {
          lastNotifShown = notif.id;
        }
      });

      try {
        await onDisplayed(lastNotifShown);
      } catch (error) {
        if (
          error.response &&
          error.response.status &&
          error.response.status === 403
        ) {
          window.location.href = '/';
        } else {
          throw error;
        }
      }
    }
  };

  const clearNotificationLoadInterval = () => {
    if (
      currentNotificationsLoadIntervalId &&
      currentNotificationsLoadIntervalId.current
    ) {
      clearInterval(currentNotificationsLoadIntervalId.current);
    }
  };

  const loadNotifications = async () => {
    if (!loading.current) {
      try {
        loading.current = true;
        await onLoad();
        loading.current = false;
      } catch (error) {
        loading.current = false;
        if (
          error.response &&
          error.response.status &&
          error.response.status === 403
        ) {
          window.location.href = '/';
        } else {
          throw error;
        }
      }
    }
  };

  const readNotificationContent = () => (event: Event) => {
    event.stopPropagation();
    setShowNav(false);
    cancelHover(false, 'notification');
  };

  useEffect(() => {
    async function init() {
      try {
        if (loggedIn) {
          currentNotificationsLoadIntervalId.current = setInterval(
            loadNotifications,
            reloadNotificationsDelayMs,
          );
          await loadNotifications();
        }
      } catch (error) {
        if (
          error.response &&
          error.response.status &&
          error.response.status === 403
        ) {
          window.location.href = '/';
        } else {
          throw error;
        }
      }
    }
    init();

    return () => {
      clearNotificationLoadInterval();
    };
  }, []);

  useEffect(() => {
    window.addEventListener('click', showNotificationCallback);
    window.addEventListener('touchstart', showNotificationCallback);
    return () => {
      window.removeEventListener('click', showNotificationCallback);
      window.removeEventListener('touchstart', showNotificationCallback);
    };
  }, [showNotificationCallback]);

  return (
    <div ref={notificationRef}>
      <NavItemButton onClick={openNotifications} dataTestId="notif-open-list">
        <NotificationMenu />
      </NavItemButton>
      {showNav && (
        <NotificationContent
          readNotificationContent={readNotificationContent}
        />
      )}
    </div>
  );
};
